import { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useUserContext } from '../context/UserContext';
import { ROUTES } from '../routes';
import { isAdmin } from '../lib/roles';
import { usePaginatedComplaintsWithStats, useStoresQuery, useCategories } from '../hooks';
import { EmptyState, EmptySearchState, Autocomplete, Skeleton, Pagination } from '../components/ui';
import {
  DateRangeFilter,
  DEFAULT_DATE_RANGE,
  ExportButton,
  StoreFilter,
  dateRangeToParams,
  paramsToDateRange,
} from '../components/complaints';
import type { DateRange, StoreFilterValue } from '../components/complaints';
import type { ComplaintStatus, Complaint } from '../api';
import {
  Search,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Plus,
  Calendar,
  Building2,
  Store,
  ClipboardList,
  BarChart3,
} from 'lucide-react';

const SKELETON_INITIAL_ROWS = 8;
const PAGE_SIZE = 10;

function ComplaintsListSkeletonRow({
  rowIndex,
  inline,
}: {
  rowIndex: number;
  /** When true, parent uses divide-y (append to list) */
  inline?: boolean;
}) {
  return (
    <div
      className={`animate-complaint-skeleton-row ${inline ? '' : 'border-b border-gray-100 last:border-b-0'}`}
      style={{ animationDelay: `${Math.min(rowIndex, 10) * 42}ms` }}
    >
      <div className="hidden lg:grid lg:grid-cols-[1fr_150px_150px_120px_100px] gap-4 px-6 py-4 items-center">
        <div className="space-y-2 min-w-0">
          <Skeleton className="h-5 w-56 max-w-full" />
          <Skeleton className="h-3 w-28" />
          <Skeleton className="h-4 w-32" />
        </div>
        <Skeleton className="h-4 w-14" />
        <Skeleton className="h-4 w-full max-w-[7rem]" />
        <Skeleton className="h-7 w-[5.5rem] rounded-full" />
        <Skeleton className="h-4 w-[4.5rem]" />
      </div>
      <div className="lg:hidden p-4 space-y-3">
        <div className="flex justify-between gap-3">
          <div className="flex-1 min-w-0 space-y-2">
            <Skeleton className="h-5 w-4/5 max-w-full" />
            <Skeleton className="h-3 w-3/5 max-w-full" />
            <Skeleton className="h-4 w-2/5 max-w-full" />
          </div>
          <Skeleton className="h-7 w-24 rounded-full flex-shrink-0" />
        </div>
        <div className="flex flex-wrap gap-3">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-4 w-24" />
        </div>
      </div>
    </div>
  );
}

function ComplaintsListSkeleton({ rows, 'data-testid': testId = 'complaints-loading' }: { rows: number; 'data-testid'?: string }) {
  return (
    <div
      data-testid={testId}
      className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm"
      aria-busy="true"
      aria-label="Loading complaints"
    >
      <div className="hidden lg:grid lg:grid-cols-[1fr_150px_150px_120px_100px] gap-4 px-6 py-3 bg-gray-50 border-b border-gray-200">
        <Skeleton className="h-3 w-20" />
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-3 w-20" />
        <Skeleton className="h-3 w-14" />
        <Skeleton className="h-3 w-12" />
      </div>
      {Array.from({ length: rows }).map((_, i) => (
        <ComplaintsListSkeletonRow key={i} rowIndex={i} />
      ))}
    </div>
  );
}

// Compact stat card for mobile horizontal scroll
interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  iconBg: string;
  textColor: string;
  isLoading?: boolean;
  isActive?: boolean;
  onClick: () => void;
  testId?: string;
}

function StatCard({ title, value, icon, iconBg, textColor, isLoading, isActive, onClick, testId }: StatCardProps) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`flex-shrink-0 flex items-center gap-3 bg-white rounded-xl border px-4 py-3 hover:shadow-md transition-all ${
        isActive ? 'border-gray-900 ring-2 ring-gray-100' : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      <div className={`w-10 h-10 rounded-lg ${iconBg} flex items-center justify-center flex-shrink-0`}>
        {icon}
      </div>
      <div className="text-left">
        {isLoading ? (
          <div className="h-6 w-8 bg-gray-200 rounded animate-pulse" />
        ) : (
          <div className={`text-xl font-bold ${textColor}`}>{value}</div>
        )}
        <div className="text-xs text-gray-500 whitespace-nowrap">{title}</div>
      </div>
    </button>
  );
}

const statusConfig: Record<ComplaintStatus, { label: string; color: string; icon: React.ReactNode }> = {
  submitted: {
    label: 'Submitted',
    color: 'bg-indigo-50 text-indigo-800',
    icon: <Clock className="w-3.5 h-3.5" />,
  },
  under_review: {
    label: 'Under Review',
    color: 'bg-yellow-50 text-yellow-800',
    icon: <AlertCircle className="w-3.5 h-3.5" />,
  },
  approved: {
    label: 'Approved',
    color: 'bg-green-100 text-green-700',
    icon: <CheckCircle2 className="w-3.5 h-3.5" />,
  },
  rejected: {
    label: 'Rejected',
    color: 'bg-red-100 text-red-700',
    icon: <XCircle className="w-3.5 h-3.5" />,
  },
};

function StatusBadge({ status }: { status: ComplaintStatus }) {
  const config = statusConfig[status];
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}
    >
      {config.icon}
      {config.label}
    </span>
  );
}

function formatDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatCategory(category: string): string {
  return category
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// Stat filter type - matches actual complaint statuses
type StatFilter = 'total' | 'submitted' | 'under_review' | 'resolved' | null;

function looksLikeReferenceSearch(query: string): boolean {
  const q = query.trim();
  if (!q) return false;
  // Reference format: {brandId}-{YY}-{MM}-{increment}
  // Support prefix searches like "TBC-26-01" too.
  return /^[A-Za-z0-9]{2,10}-\d{2}-\d{2}(?:-\d{1,3})?$/.test(q);
}

const VALID_STATUSES = ['all', 'submitted', 'under_review', 'approved', 'rejected'] as const;
const FILTER_PARAM_KEYS = ['search', 'status', 'datePreset', 'dateStart', 'dateEnd', 'stores'];

function getFiltersStorageKey(brandId: string | undefined | null): string {
  return `byte-qc-complaints-filters${brandId ? `-${brandId}` : ''}`;
}

function hasFilterParams(params: URLSearchParams): boolean {
  return FILTER_PARAM_KEYS.some((key) => params.has(key));
}

export function ComplaintsList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, brandId } = useUserContext();

  const { data: categoriesData } = useCategories(!!user && !!brandId, brandId ?? undefined);
  const categoryLabels = useMemo(
    () =>
      (categoriesData ?? []).reduce<Record<string, string>>((acc, c) => {
        acc[c.categoryId] = c.label;
        return acc;
      }, {}),
    [categoriesData]
  );

  // Restore filters from localStorage when URL has no filter params (e.g. fresh visit, new session)
  useEffect(() => {
    if (!brandId) return;
    if (hasFilterParams(searchParams)) return;

    try {
      const stored = localStorage.getItem(getFiltersStorageKey(brandId));
      if (stored) {
        const parsed = JSON.parse(stored) as Record<string, string>;
        // Preserve existing params (e.g. `page`) when restoring filters.
        const next = new URLSearchParams(searchParams);
        Object.entries(parsed).forEach(([k, v]) => {
          if (v != null && v !== '') next.set(k, v);
        });
        if (next.toString() !== searchParams.toString()) {
          setSearchParams(next, { replace: true });
        }
      }
    } catch {
      // Ignore invalid stored data
    }
  }, [brandId, searchParams, setSearchParams]);

  // Derive filter state from URL (persisted across refresh/navigation)
  const searchQuery = searchParams.get('search') ?? '';
  const statusFilter =
    (VALID_STATUSES.includes(searchParams.get('status') as (typeof VALID_STATUSES)[number])
      ? searchParams.get('status')
      : 'all') as ComplaintStatus | 'all';
  const dateRange = useMemo(() => paramsToDateRange(searchParams), [searchParams]);
  const storeFilter: StoreFilterValue = useMemo(() => {
    const s = searchParams.get('stores');
    if (!s) return null;
    const ids = s.split(',').map((x) => x.trim()).filter(Boolean);
    return ids.length > 0 ? ids : null;
  }, [searchParams]);

  const [activeStatFilter, setActiveStatFilter] = useState<StatFilter>(null);

  const updateFilters = useCallback(
    (updates: {
      search?: string;
      status?: ComplaintStatus | 'all';
      dateRange?: DateRange;
      storeFilter?: StoreFilterValue;
    }) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (updates.search !== undefined) {
            if (updates.search) next.set('search', updates.search);
            else next.delete('search');
          }
          if (updates.status !== undefined) {
            if (updates.status && updates.status !== 'all') next.set('status', updates.status);
            else next.delete('status');
          }
          if (updates.dateRange !== undefined) {
            if (
              updates.dateRange.startDate == null &&
              updates.dateRange.endDate == null &&
              updates.dateRange.label === 'All Time'
            ) {
              next.delete('datePreset');
              next.delete('dateStart');
              next.delete('dateEnd');
            } else {
              const dateParams = dateRangeToParams(updates.dateRange);
              Object.entries(dateParams).forEach(([k, v]) => next.set(k, v));
            }
          }
          if (updates.storeFilter !== undefined) {
            if (updates.storeFilter && updates.storeFilter.length > 0) {
              next.set('stores', updates.storeFilter.join(','));
            } else {
              next.delete('stores');
            }
          }
          // If no filter params remain, clear localStorage so restore effect doesn't re-apply old values
          if (!hasFilterParams(next)) {
            try {
              localStorage.removeItem(getFiltersStorageKey(brandId));
            } catch { /* ignore */ }
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams, brandId]
  );

  // Persist filters to localStorage whenever URL params change (for cross-session persistence)
  useEffect(() => {
    if (!brandId) return;
    const params: Record<string, string> = {};
    FILTER_PARAM_KEYS.forEach((key) => {
      const v = searchParams.get(key);
      if (v != null && v !== '') params[key] = v;
    });
    try {
      localStorage.setItem(getFiltersStorageKey(brandId), JSON.stringify(params));
    } catch {
      // Ignore quota errors
    }
  }, [brandId, searchParams]);

  const isVendor = user?.userType === 'vendor';
  const canSubmitComplaints = !isVendor;
  const hasAdminAccess = isAdmin(user?.roles);
  const brandReady = !!user && !!brandId;

  const { data: storesData } = useStoresQuery(brandReady && !isVendor);
  const stores = storesData?.data ?? [];

  const trimmedSearch = searchQuery.trim();
  const referenceSearch = looksLikeReferenceSearch(trimmedSearch) ? trimmedSearch.toUpperCase() : undefined;

  // Fetch the full filtered set (walks cursor API) so we can render numbered
  // pagination with an exact total and arbitrary page jumps. Stats come back on
  // the first underlying request.
  const {
    data: paginatedData,
    isLoading,
    isFetching,
    error,
  } = usePaginatedComplaintsWithStats(
    {
      status: statusFilter === 'all' ? undefined : statusFilter,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      search: referenceSearch,
      storeNumbers: storeFilter ?? undefined,
    },
    brandReady
  );
  const complaints = useMemo(() => paginatedData?.complaints ?? [], [paginatedData]);
  const statsData = paginatedData?.stats;
  const stats = {
    total: statsData?.total ?? 0,
    submitted: statsData?.byStatus?.submitted ?? 0,
    underReview: statsData?.byStatus?.under_review ?? 0,
    resolved: statsData?.resolved ?? 0,
  };
  const statsLoading = isLoading;

  // Client-side search filter
  const filteredComplaints = useMemo(
    () =>
      complaints.filter((complaint: Complaint) => {
        if (!trimmedSearch) return true;
        const query = trimmedSearch.toLowerCase();
        return (
          complaint.productName.toLowerCase().includes(query) ||
          complaint.storeName.toLowerCase().includes(query) ||
          complaint.storeId.toLowerCase().includes(query) ||
          (complaint.champsNumber?.toLowerCase().includes(query) ?? false) ||
          complaint.supplierName.toLowerCase().includes(query) ||
          complaint.referenceNumber.toLowerCase().includes(query)
        );
      }),
    [complaints, trimmedSearch]
  );

  // Current page (1-based) persisted in the URL.
  const currentPage = Math.max(1, parseInt(searchParams.get('page') ?? '1', 10) || 1);
  const totalPages = Math.max(1, Math.ceil(filteredComplaints.length / PAGE_SIZE));

  const setPage = useCallback(
    (page: number, options?: { replace?: boolean }) => {
      // Push a history entry for user-initiated page changes so the browser
      // Back button steps through pages. Automatic clamps pass replace=true.
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (page <= 1) next.delete('page');
          else next.set('page', String(page));
          return next;
        },
        { replace: options?.replace ?? false }
      );
      // Scroll to the bottom after the new page renders so the pagination
      // controls stay in view. Double rAF waits for layout to settle.
      if (typeof window !== 'undefined') {
        requestAnimationFrame(() =>
          requestAnimationFrame(() =>
            window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' })
          )
        );
      }
    },
    [setSearchParams]
  );

  // Clamp the page if the result set shrinks (e.g. after filtering).
  // Skip while loading — totalPages is momentarily 1 before data arrives, which
  // would otherwise reset a deep-linked page (e.g. ?page=5) back to page 1.
  useEffect(() => {
    if (isLoading) return;
    if (currentPage > totalPages) {
      setPage(totalPages, { replace: true });
    }
  }, [isLoading, currentPage, totalPages, setPage]);

  const pageStartIndex = (currentPage - 1) * PAGE_SIZE;
  const pageComplaints = filteredComplaints.slice(pageStartIndex, pageStartIndex + PAGE_SIZE);

  const handleViewComplaint = (complaintId: string) => {
    navigate(`/complaints/${complaintId}`);
  };

  // Handle stat card click to filter
  const handleStatClick = (filter: StatFilter) => {
    setActiveStatFilter(filter);
    if (filter === 'total' || filter === null) {
      updateFilters({ status: 'all' });
    } else if (filter === 'submitted') {
      updateFilters({ status: 'submitted' });
    } else if (filter === 'under_review') {
      updateFilters({ status: 'under_review' });
    } else if (filter === 'resolved') {
      updateFilters({ status: 'approved' });
    }
  };

  // Clear all filters
  const clearFilters = () => {
    try {
      localStorage.removeItem(getFiltersStorageKey(brandId));
    } catch { /* ignore */ }
    updateFilters({
      search: '',
      status: 'all',
      dateRange: DEFAULT_DATE_RANGE,
      storeFilter: null,
    });
    setActiveStatFilter(null);
  };

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Complaints</h1>
            <p className="mt-1 text-sm text-red-500">Error loading complaints</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-red-200 p-6">
          <p className="text-red-600">
            {error instanceof Error ? error.message : 'Failed to load complaints'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats - horizontal scroll on mobile, grid on desktop */}
      <div className="relative">
        <div className="flex gap-3 overflow-x-auto pb-1 -mx-4 px-4 sm:mx-0 sm:px-0 sm:grid sm:grid-cols-4 sm:overflow-visible scrollbar-thin">
        <StatCard
          title="Total"
          value={stats.total}
          icon={<ClipboardList className="w-5 h-5 text-gray-600" />}
          iconBg="bg-gray-100"
          textColor="text-gray-900"
          isLoading={statsLoading}
          isActive={activeStatFilter === 'total'}
          onClick={() => handleStatClick(activeStatFilter === 'total' ? null : 'total')}
          testId="complaints-card-stat-total"
        />
        <StatCard
          title="Submitted"
          value={stats.submitted}
          icon={<Clock className="w-5 h-5 text-yellow-600" />}
          iconBg="bg-yellow-100"
          textColor="text-yellow-600"
          isLoading={statsLoading}
          isActive={activeStatFilter === 'submitted'}
          onClick={() => handleStatClick(activeStatFilter === 'submitted' ? null : 'submitted')}
          testId="complaints-card-stat-submitted"
        />
        <StatCard
          title="Under Review"
          value={stats.underReview}
          icon={<AlertCircle className="w-5 h-5 text-orange-600" />}
          iconBg="bg-orange-100"
          textColor="text-orange-600"
          isLoading={statsLoading}
          isActive={activeStatFilter === 'under_review'}
          onClick={() => handleStatClick(activeStatFilter === 'under_review' ? null : 'under_review')}
          testId="complaints-card-stat-under-review"
        />
        <StatCard
          title="Resolved"
          value={stats.resolved}
          icon={<CheckCircle2 className="w-5 h-5 text-green-600" />}
          iconBg="bg-green-100"
          textColor="text-green-600"
          isLoading={statsLoading}
          isActive={activeStatFilter === 'resolved'}
          onClick={() => handleStatClick(activeStatFilter === 'resolved' ? null : 'resolved')}
          testId="complaints-card-stat-resolved"
        />
        </div>
        {/* Scroll fade hint - mobile only */}
        <div className="pointer-events-none absolute right-0 top-0 bottom-1 w-8 bg-gradient-to-l from-gray-50 to-transparent sm:hidden" />
      </div>

      {/* Search and filters - compact toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Search */}
        <div className="relative flex-1 min-w-[140px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => updateFilters({ search: e.target.value })}
            data-testid="complaints-input-search"
            className="w-full pl-9 pr-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent"
          />
        </div>

        {/* Date range filter */}
        <DateRangeFilter value={dateRange} onChange={(r) => updateFilters({ dateRange: r })} />

        {/* Restaurant filter - internal users only, hide when single store */}
        {!isVendor && stores.length > 1 && (
          <StoreFilter
            stores={stores}
            value={storeFilter}
            onChange={(v) => updateFilters({ storeFilter: v })}
            disabled={stores.length === 0}
          />
        )}

        {/* Status filter */}
        <Autocomplete
          options={[
            { value: 'all' as const, label: 'All Statuses' },
            ...Object.entries(statusConfig).map(([key, config]) => ({
              value: key as ComplaintStatus,
              label: config.label,
            })),
          ]}
          value={statusFilter}
          onChange={(v) => {
            updateFilters({ status: v || 'all' });
            setActiveStatFilter(null);
          }}
          placeholder="Status"
          clearable={false}
          filterable={false}
          data-testid="complaints-select-status"
          id="complaints-status-filter"
          className="min-w-[140px]"
        />

        {/* Spacer - pushes action buttons to the right on larger screens */}
        <div className="hidden sm:block flex-1" />

        {/* Action buttons - icon only on mobile */}
        <div className="flex items-center gap-2">
          {/* Analytics link - internal users only */}
          {!isVendor && (
            <button
              onClick={() => navigate(ROUTES.ANALYTICS)}
              data-testid="complaints-btn-analytics"
              aria-label="View analytics"
              className="inline-flex items-center justify-center gap-2 p-2.5 min-h-[44px] min-w-[44px] sm:px-3 sm:py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              title="Analytics"
            >
              <BarChart3 className="w-4 h-4" />
              <span className="hidden sm:inline">Analytics</span>
            </button>
          )}

          {/* Export button - admin only */}
          {hasAdminAccess && (
            <ExportButton
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              status={statusFilter}
              storeNumbers={storeFilter}
              search={referenceSearch}
            />
          )}

          {/* Submit button - internal users only, primary action */}
          {canSubmitComplaints && (
            <button
              onClick={() => navigate(ROUTES.SUBMIT_COMPLAINT)}
              data-testid="complaints-btn-new"
              aria-label="Submit new complaint"
              className="inline-flex items-center justify-center gap-2 p-2.5 min-h-[44px] min-w-[44px] sm:px-4 sm:py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
              title="New Complaint"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">New</span>
            </button>
          )}
        </div>
      </div>

      {/* Initial load: skeleton table (reads as “almost there” vs blank spinner) */}
      {isLoading ? (
        <ComplaintsListSkeleton rows={SKELETON_INITIAL_ROWS} />
      ) : filteredComplaints.length === 0 ? (
        <div data-testid="complaints-empty-state" className="bg-white rounded-xl border border-gray-200">
          {searchQuery || statusFilter !== 'all' || dateRange.startDate !== undefined || storeFilter !== null ? (
            // Search/filter empty state
            <EmptySearchState
              query={searchQuery}
              entityName="complaints"
              onClearFilters={clearFilters}
            />
          ) : isVendor ? (
            // Vendor empty state - no complaints assigned
            <EmptyState
              icon={ClipboardList}
              title="No complaints assigned"
              description="Complaints assigned to your organization will appear here. Check back later or contact your administrator."
            />
          ) : (
            // Internal user empty state - first time use
            <EmptyState
              icon={ClipboardList}
              title="No complaints yet"
              description="Get started by submitting your first quality complaint. Track issues with distributors and ensure product quality."
              action={
                canSubmitComplaints
                  ? {
                      label: 'Submit First Complaint',
                      onClick: () => navigate(ROUTES.SUBMIT_COMPLAINT),
                      icon: Plus,
                    }
                  : undefined
              }
            />
          )}
        </div>
      ) : (
        <div className="relative bg-white rounded-xl border border-gray-200 overflow-hidden">
          {/* Background refetch (e.g. window focus): thin progress — feels responsive */}
          {isFetching && !isLoading && filteredComplaints.length > 0 && (
            <div
              className="absolute top-0 left-0 right-0 h-1 z-10 overflow-hidden bg-gray-100/90 rounded-t-xl"
              aria-hidden
            >
              <div className="h-full w-[32%] rounded-full bg-primary-500/90 animate-complaint-list-progress" />
            </div>
          )}
          {/* Table header - Desktop */}
          <div className="hidden lg:grid lg:grid-cols-[1fr_150px_150px_120px_100px] gap-4 px-6 py-3 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500 uppercase tracking-wider">
            <div>Complaint</div>
            <div>Restaurant</div>
            <div>Distributor</div>
            <div>Status</div>
            <div>Date</div>
          </div>

          {/* Table body */}
          <div className="divide-y divide-gray-200">
            {pageComplaints.map((complaint: Complaint, index: number) => (
              <div
                key={complaint.complaintId}
                onClick={() => handleViewComplaint(complaint.complaintId)}
                data-testid={`complaints-item-${complaint.complaintId}`}
                className="group hover:bg-gray-50 transition-colors cursor-pointer animate-complaint-row-enter"
                style={{ animationDelay: `${(index % 25) * 18}ms` }}
              >
                {/* Desktop row */}
                <div className="hidden lg:grid lg:grid-cols-[1fr_150px_150px_120px_100px] gap-4 px-6 py-4 items-center">
                  <div>
                    <p className="font-medium text-gray-900 truncate">{complaint.productName}</p>
                    <p className="text-xs text-primary-600 font-mono font-semibold">{complaint.referenceNumber}</p>
                    <p className="text-sm text-gray-500">
                      {categoryLabels[complaint.category] ?? formatCategory(complaint.category)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <Store className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span
                      className="truncate"
                      title={`${complaint.storeName} — AIS #${complaint.storeId}`}
                    >
                      {complaint.champsNumber || complaint.storeId}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <Building2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="truncate" title={complaint.supplierName}>
                      {complaint.supplierName}
                    </span>
                  </div>
                  <StatusBadge status={complaint.status} />
                  <div className="flex items-center gap-1.5 text-sm text-gray-500">
                    <Calendar className="w-4 h-4 flex-shrink-0" />
                    {formatDate(complaint.submittedAt)}
                  </div>
                </div>

                {/* Mobile card */}
                <div className="lg:hidden p-4">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{complaint.productName}</p>
                      <p className="text-xs text-primary-600 font-mono font-semibold">{complaint.referenceNumber}</p>
                      <p className="text-sm text-gray-500">
                        {categoryLabels[complaint.category] ?? formatCategory(complaint.category)}
                      </p>
                    </div>
                    <StatusBadge status={complaint.status} />
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-500">
                    <span
                      className="flex items-center gap-1.5"
                      title={`${complaint.storeName} — AIS #${complaint.storeId}`}
                    >
                      <Store className="w-4 h-4" />
                      {complaint.champsNumber || complaint.storeId}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Building2 className="w-4 h-4" />
                      {complaint.supplierName}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Calendar className="w-4 h-4" />
                      {formatDate(complaint.submittedAt)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Numbered pagination footer */}
      {filteredComplaints.length > 0 && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-4 py-3 bg-white rounded-lg border border-gray-200">
          <p className="text-sm text-gray-500">
            Showing{' '}
            <span className="font-medium">{pageStartIndex + 1}</span>–
            <span className="font-medium">{pageStartIndex + pageComplaints.length}</span> of{' '}
            <span className="font-medium">{filteredComplaints.length}</span>
            {' '}complaint{filteredComplaints.length !== 1 ? 's' : ''}
          </p>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setPage}
            data-testid="complaints-pagination"
          />
        </div>
      )}
    </div>
  );
}
